import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { C } from '../theme'

const SCHRITTE = [
  { nr: 1, name: 'Übernahme' },
  { nr: 2, name: 'Ersatzteile' },
  { nr: 3, name: 'Demontage', mitarbeiter: true },
  { nr: 4, name: 'Lackvorbereitung' },
  { nr: 5, name: 'Lackierung' },
  { nr: 6, name: 'Lackkontrolle' },
  { nr: 7, name: 'Montage' },
  { nr: 8, name: 'Politur' },
  { nr: 9, name: 'Aufbereitung' },
  { nr: 10, name: 'Übergabe' },
]

const PROBLEM_SCHRITTE = [5, 6, 8]
const FASTCARS_SCHRITTE = [1, 8]

export default function Tracker({ fahrzeug, onClose, userRole, mitarbeiter, isMobile }) {
  const [current, setCurrent] = useState(fahrzeug)
  const [kundeEdit, setKundeEdit] = useState(false)
  const [kundeData, setKundeData] = useState({ kunde: fahrzeug.kunde || '', telefon: fahrzeug.telefon || '', farbe: fahrzeug.farbe || '' })
  const [kundeSaving, setKundeSaving] = useState(false)
  const [problem, setProblem] = useState(false)
  const [problemText, setProblemText] = useState('')
  const [problemZiel, setProblemZiel] = useState(1)
  const [teileBlock, setTeileBlock] = useState(false)
  const [loading, setLoading] = useState(false)
  const [historie, setHistorie] = useState([])
  const [notiz, setNotiz] = useState('')
  const [kuerzel, setKuerzel] = useState('')
  const [showAlleNotizen, setShowAlleNotizen] = useState(false)
  const [übergabeDatum, setÜbergabeDatum] = useState(new Date().toISOString().split('T')[0])
  const [übergabeOk, setÜbergabeOk] = useState(false)
  const [fastcarsSchicken, setFastcarsSchicken] = useState(false)
  const [fastcarsNotiz, setFastcarsNotiz] = useState('')
  const [wunschdatum, setWunschdatum] = useState(current.wunschdatum || '')
  const [wunschdatumEdit, setWunschdatumEdit] = useState(false)

  useEffect(() => {
    loadHistorie()
    const channel = supabase
      .channel(`fahrzeug-${fahrzeug.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'fahrzeuge', filter: `id=eq.${fahrzeug.id}` },
        (payload) => setCurrent(payload.new))
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [fahrzeug.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistorie = async () => {
    const { data } = await supabase
      .from('schritt_historie').select('*').eq('fahrzeug_id', fahrzeug.id)
      .order('created_at', { ascending: false })
    setHistorie(data || [])
  }

  const log = async (schritt, typ, text) => {
    await supabase.from('schritt_historie').insert({ fahrzeug_id: fahrzeug.id, schritt, typ, text, mitarbeiter })
  }

  const saveWunschdatum = async () => {
    await supabase.from('fahrzeuge').update({ wunschdatum: wunschdatum || null }).eq('id', fahrzeug.id)
    await log(current.schritt, 'termin', `📅 Wunschtermin gesetzt: ${new Date(wunschdatum).toLocaleDateString('de-DE')}`)
    setWunschdatumEdit(false)
  }

  const fastcarsZurueck = async () => {
    const naechster = (current.fastcars_von_schritt || current.schritt) + 1
    await supabase.from('fahrzeuge').update({
      fastcars: false, fastcars_notiz: '', fastcars_von_schritt: null, schritt: naechster,
    }).eq('id', fahrzeug.id)
    await log(current.schritt, 'fastcars', `🔙 Von FastCars zurückgekehrt → Schritt ${naechster}`)
    if (notiz) await log(current.schritt, 'notiz', `📝 ${notiz}`)
    setNotiz('')
  }

  const naechsterSchritt = async () => {
    if (loading) return
    const schritt = current.schritt

    if (schritt === 2 && teileBlock) { alert('Ersatzteile fehlen!'); return }

    if (fastcarsSchicken) {
      if (!FASTCARS_SCHRITTE.includes(schritt)) {
        if (!window.confirm('FastCars-Weiterleitung ist nur nach Schritt 1 oder 8 vorgesehen. Trotzdem?')) return
      }
      setLoading(true)
      await supabase.from('fahrzeuge').update({ fastcars: true, fastcars_notiz: fastcarsNotiz, fastcars_von_schritt: schritt }).eq('id', fahrzeug.id)
      await log(schritt, 'fastcars', `🔧 Zu FastCars geschickt${fastcarsNotiz ? `: ${fastcarsNotiz}` : ''}`)
      if (notiz) await log(schritt, 'notiz', `📝 ${notiz}`)
      setFastcarsSchicken(false); setFastcarsNotiz(''); setNotiz('')
      setLoading(false)
      return
    }

    if (PROBLEM_SCHRITTE.includes(schritt) && problem) {
      if (!problemText) { alert('Bitte Problem beschreiben!'); return }
      setLoading(true)
      await supabase.from('fahrzeuge').update({ schritt: problemZiel, problem: true, problem_text: problemText }).eq('id', fahrzeug.id)
      await log(schritt, 'problem', `⚠ Problem: ${problemText} → Schritt ${problemZiel}`)
      if (notiz) await log(schritt, 'notiz', `📝 ${notiz}`)
      setProblem(false); setProblemText(''); setNotiz('')
      setLoading(false)
      return
    }

    if (schritt === 10) {
      if (!übergabeOk) { alert('Bitte Übergabe bestätigen!'); return }
      setLoading(true)
      await supabase.from('fahrzeuge').update({ übergabe_ok: true, übergabe_datum: übergabeDatum }).eq('id', fahrzeug.id)
      await log(10, 'übergabe', `✅ Übergeben am ${new Date(übergabeDatum).toLocaleDateString('de-DE')}`)
      if (notiz) await log(10, 'notiz', `📝 ${notiz}`)
      setLoading(false)
      return
    }

    const update = { schritt: schritt + 1, problem: false }
    if (schritt === 3 && kuerzel) update.mitarbeiter = kuerzel
    setLoading(true)
    await supabase.from('fahrzeuge').update(update).eq('id', fahrzeug.id)
    const msg = `✓ Schritt ${schritt} (${SCHRITTE[schritt - 1].name}) abgeschlossen${kuerzel ? ` [${kuerzel}]` : ''}`
    await log(schritt, 'schritt', msg)
    if (notiz) await log(schritt, 'notiz', `📝 ${notiz}`)
    setNotiz(''); setKuerzel('')
    setLoading(false)
  }

  const saveKunde = async () => {
    setKundeSaving(true)
    await supabase.from('fahrzeuge').update(kundeData).eq('id', fahrzeug.id)
    setCurrent(prev => ({ ...prev, ...kundeData }))
    setKundeEdit(false)
    setKundeSaving(false)
  }

  const deadline = current.wunschdatum ? Math.floor((new Date(current.wunschdatum) - new Date()) / 86400000) : null
  const vorherNotizen = historie.filter(h => h.typ === 'notiz')
  const aktuellerSchritt = SCHRITTE[current.schritt - 1]

  const modalStyle = isMobile
    ? { ...styles.modal, width: '100%', maxWidth: '100%', height: '100%', maxHeight: '100vh', borderRadius: 0, padding: 16, boxSizing: 'border-box' }
    : styles.modal

  return (
    <div style={styles.overlay}>
      <div style={modalStyle}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20 }}>{current.kennzeichen}</h2>
            <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
              {current.marke} {current.modell} · {current.farbe || '–'} · {current.kunde || '–'}
              {current.telefon && <span style={{ marginLeft: 6, color: '#4299e1' }}>📞 {current.telefon}</span>}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={{ ...styles.notizenBtn, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }} onClick={() => { setKundeEdit(!kundeEdit); }}>
              ✏️ Kunde
            </button>
            {vorherNotizen.length > 0 && (
              <button style={styles.notizenBtn} onClick={() => setShowAlleNotizen(!showAlleNotizen)}>
                📋 {vorherNotizen.length} Notizen
              </button>
            )}
            <button onClick={onClose} style={styles.closeBtn}>✕</button>
          </div>
        </div>

        {/* Kundendaten Edit Panel */}
        {kundeEdit && (
          <div style={{ padding: '14px 20px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
              {[['Kundenname','kunde'],['Telefon','telefon'],['Farbe','farbe']].map(([lbl, key]) => (
                <div key={key}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>{lbl}</div>
                  <input
                    style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                    value={kundeData[key]}
                    onChange={e => setKundeData({ ...kundeData, [key]: e.target.value })}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setKundeEdit(false)} style={{ padding: '6px 14px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12 }}>Abbrechen</button>
              <button onClick={saveKunde} disabled={kundeSaving} style={{ padding: '6px 16px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                {kundeSaving ? 'Speichern...' : '✓ Speichern'}
              </button>
            </div>
          </div>
        )}

        {/* Wunschdatum */}
        <div style={styles.terminBox}>
          {wunschdatumEdit ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Wunschtermin:</span>
              <input type="date" style={styles.smallInput} value={wunschdatum} onChange={e => setWunschdatum(e.target.value)} />
              <button style={styles.saveSmallBtn} onClick={saveWunschdatum}>✓ Speichern</button>
              <button style={styles.cancelSmallBtn} onClick={() => setWunschdatumEdit(false)}>✕</button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13 }}>
                <strong>Wunschtermin:</strong>{' '}
                {current.wunschdatum
                  ? <span style={{ color: deadline < 0 ? '#c53030' : deadline <= 2 ? '#d69e2e' : '#276749', fontWeight: 700 }}>
                      {new Date(current.wunschdatum).toLocaleDateString('de-DE')}
                      {deadline !== null && <span style={{ marginLeft: 6, fontSize: 12 }}>
                        {deadline < 0 ? `(${Math.abs(deadline)} Tage überfällig!)` : deadline === 0 ? '(Heute!)' : `(noch ${deadline} Tage)`}
                      </span>}
                    </span>
                  : <span style={{ color: '#999' }}>Nicht gesetzt</span>}
              </span>
              <button style={styles.editBtn} onClick={() => setWunschdatumEdit(true)}>✏</button>
            </div>
          )}
        </div>

        {/* Warnungen */}
        {current.problem && <div style={styles.problemBanner}>⚠ Problem: {current.problem_text}</div>}
        {current.fastcars && (
          <div style={styles.fastcarsBanner}>
            🔧 Bei FastCars{current.fastcars_notiz ? `: ${current.fastcars_notiz}` : ''} (seit Schritt {current.fastcars_von_schritt})
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <textarea style={{ ...styles.textarea, width: 200, height: 36, margin: 0 }} placeholder="Rückkehr-Notiz..." value={notiz} onChange={e => setNotiz(e.target.value)} />
              <button style={styles.zurueckBtn} onClick={fastcarsZurueck}>
                🔙 Zurückrufen → Schritt {(current.fastcars_von_schritt || current.schritt) + 1}
              </button>
            </div>
          </div>
        )}

        {/* Alle Notizen */}
        {showAlleNotizen && (
          <div style={styles.alleNotizenBox}>
            <h4 style={{ margin: '0 0 8px' }}>Alle Notizen</h4>
            {vorherNotizen.map(h => (
              <div key={h.id} style={styles.notizItem}>
                <span style={{ color: '#999', fontSize: 11 }}>{new Date(h.created_at).toLocaleString('de-DE')} · Schritt {h.schritt} – {SCHRITTE[h.schritt - 1]?.name}</span>
                {h.mitarbeiter && <span style={{ color: '#4299e1', fontSize: 11 }}> [{h.mitarbeiter}]</span>}
                <div style={{ marginTop: 2 }}>{h.text.replace('📝 ', '')}</div>
              </div>
            ))}
          </div>
        )}

        {/* Schritt-Anzeige */}
        <div style={styles.stepRow}>
          {SCHRITTE.map(s => (
            <div key={s.nr} style={{
              ...styles.step,
              background: current.schritt === s.nr ? C.primary : current.schritt > s.nr ? '#48bb78' : '#e2e8f0',
              color: current.schritt === s.nr ? C.onPrimary : current.schritt > s.nr ? '#fff' : '#888',
            }}>
              <span style={{ fontWeight: 700 }}>{s.nr}</span>
              <span style={{ fontSize: 9, marginTop: 1, textAlign: 'center' }}>{s.name}</span>
            </div>
          ))}
        </div>

        {!current.fastcars && (
          <>
            <div style={styles.currentStep}>
              Schritt {current.schritt}: <strong>{aktuellerSchritt?.name}</strong>
              {current.mitarbeiter && <span style={{ color: '#4299e1', marginLeft: 8, fontSize: 13 }}>[{current.mitarbeiter}]</span>}
            </div>

            {/* Schritt 2 */}
            {current.schritt === 2 && (
              <div style={styles.infoBox}>
                <label><input type="checkbox" checked={teileBlock} onChange={e => setTeileBlock(e.target.checked)} /> Ersatzteile fehlen (blockieren)</label>
              </div>
            )}

            {/* Schritt 3 Kürzel */}
            {current.schritt === 3 && (
              <div style={styles.infoBox}>
                <label style={{ fontWeight: 600, fontSize: 13 }}>Mitarbeiter-Kürzel</label>
                <input style={{ ...styles.smallInput, display: 'block', marginTop: 6, width: 100 }} placeholder="z.B. MK" value={kuerzel} onChange={e => setKuerzel(e.target.value.toUpperCase())} maxLength={4} />
              </div>
            )}

            {/* Problem */}
            {PROBLEM_SCHRITTE.includes(current.schritt) && (
              <div style={styles.infoBox}>
                <label><input type="checkbox" checked={problem} onChange={e => setProblem(e.target.checked)} /> Problem vorhanden</label>
                {problem && (
                  <>
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <label style={{ fontSize: 13, fontWeight: 600 }}>Zurück zu Schritt:</label>
                      <select style={styles.smallInput} value={problemZiel} onChange={e => setProblemZiel(Number(e.target.value))}>
                        {SCHRITTE.filter(s => s.nr < current.schritt).map(s => (
                          <option key={s.nr} value={s.nr}>{s.nr} – {s.name}</option>
                        ))}
                      </select>
                    </div>
                    <textarea style={{ ...styles.textarea, marginTop: 8 }} placeholder="Problem beschreiben..." value={problemText} onChange={e => setProblemText(e.target.value)} rows={2} />
                  </>
                )}
              </div>
            )}

            {/* FastCars */}
            {!current.fastcars && (
              <div style={styles.infoBox}>
                <label><input type="checkbox" checked={fastcarsSchicken} onChange={e => setFastcarsSchicken(e.target.checked)} /> Zu FastCars schicken</label>
                {fastcarsSchicken && (
                  <input style={{ ...styles.smallInput, display: 'block', marginTop: 8, width: '100%', boxSizing: 'border-box' }} placeholder="Grund (z.B. Reifenwechsel)..." value={fastcarsNotiz} onChange={e => setFastcarsNotiz(e.target.value)} />
                )}
              </div>
            )}

            {/* Übergabe */}
            {current.schritt === 10 && !current.übergabe_ok && (
              <div style={styles.infoBox}>
                <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontWeight: 600 }}>Übergabe-Datum:</label>
                  <input type="date" style={styles.smallInput} value={übergabeDatum} onChange={e => setÜbergabeDatum(e.target.value)} />
                </div>
                <label><input type="checkbox" checked={übergabeOk} onChange={e => setÜbergabeOk(e.target.checked)} /> <strong>Übergabe bestätigen</strong> – Kunde hat Fahrzeug übernommen</label>
              </div>
            )}

            {/* Notiz */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Notiz</label>
              <textarea style={{ ...styles.textarea, marginTop: 4 }} placeholder="Notiz zu diesem Schritt..." value={notiz} onChange={e => setNotiz(e.target.value)} rows={2} />
            </div>

            <button style={{ ...styles.btn, background: loading ? '#999' : current.übergabe_ok ? '#48bb78' : C.primary, color: loading || current.übergabe_ok ? '#fff' : C.onPrimary }}
              onClick={naechsterSchritt} disabled={loading || !!current.übergabe_ok}>
              {current.übergabe_ok ? `✓ Übergeben am ${new Date(current.übergabe_datum).toLocaleDateString('de-DE')}`
                : current.schritt === 10 ? 'Übergabe abschließen ✓'
                : `Schritt ${current.schritt} abschließen →`}
            </button>
          </>
        )}

        {/* Verlauf */}
        <div style={styles.historieBox}>
          <h4 style={{ margin: '0 0 8px' }}>Verlauf</h4>
          {historie.length === 0 && <p style={{ color: '#999', fontSize: 13 }}>Noch kein Verlauf</p>}
          {historie.map(h => (
            <div key={h.id} style={styles.historieItem}>
              <span style={{ color: '#aaa', fontSize: 11 }}>{new Date(h.created_at).toLocaleString('de-DE')}</span>
              {h.mitarbeiter && <span style={{ color: '#4299e1', fontSize: 11 }}> [{h.mitarbeiter}]</span>}
              <span style={{ marginLeft: 6, fontSize: 13 }}>{h.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: '#fff', borderRadius: 12, padding: 24, width: '92%', maxWidth: 860, maxHeight: '92vh', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  closeBtn: { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#666' },
  notizenBtn: { background: '#ebf8ff', border: '1px solid #90cdf4', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#2b6cb0' },
  terminBox: { background: '#f7fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 12, border: '1px solid #e2e8f0' },
  smallInput: { padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 },
  saveSmallBtn: { padding: '5px 12px', background: '#48bb78', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  cancelSmallBtn: { padding: '5px 10px', background: '#e2e8f0', color: '#333', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  editBtn: { background: 'none', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', padding: '2px 8px', fontSize: 12 },
  problemBanner: { background: '#fff5f5', border: '1px solid #fc8181', color: '#c53030', borderRadius: 8, padding: '8px 14px', marginBottom: 10, fontSize: 13 },
  fastcarsBanner: { background: '#faf5ff', border: '1px solid #d6bcfa', color: '#553c9a', borderRadius: 8, padding: '10px 14px', marginBottom: 10, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  zurueckBtn: { background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
  alleNotizenBox: { background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: 8, padding: 12, marginBottom: 12 },
  notizItem: { padding: '6px 0', borderBottom: '1px solid #e2e8f0', fontSize: 13 },
  stepRow: { display: 'flex', gap: 3, marginBottom: 12, flexWrap: 'wrap' },
  step: { display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: 6, padding: '7px 5px', minWidth: 68, flex: 1, fontSize: 13 },
  currentStep: { fontSize: 14, marginBottom: 12, padding: '9px 14px', background: '#f7fafc', borderRadius: 8 },
  infoBox: { background: '#fffbeb', border: '1px solid #f6e05e', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 },
  textarea: { width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' },
  btn: { padding: '11px 22px', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14, marginBottom: 16 },
  historieBox: { borderTop: '1px solid #e2e8f0', paddingTop: 14 },
  historieItem: { padding: '5px 0', borderBottom: '1px solid #f5f5f5' },
}
